import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import marineTelemedicineDevices from "@/assets/marine-telemedicine-devices.png";
import pregnancyMonitoringApp from "@/assets/pregnancy-monitoring-app.png";
import emergencyPanicButton from "@/assets/emergency-panic-button.png";
import digitalOutpatientClinic from "@/assets/digital-outpatient-clinic.png";
import chronicDiseaseTelemedicine from "@/assets/chronic-disease-telemedicine.png";
import ehealthWellnessTourism from "@/assets/ehealth-wellness-tourism.png";
import behaviouralChangeSolutions from "@/assets/behavioural-change-solutions.png";
import scientificWellnessApp from "@/assets/scientific-wellness-app.png";
import aiDecisionSupport from "@/assets/ai-decision-support.png";
import insurtechDashboard from "@/assets/insurtech-dashboard.png";
import ecoHealthAnalytics from "@/assets/eco-health-analytics.png";
interface ServiceData {
  title: string;
  description: string;
  features: Array<{
    title: string;
    description: string;
  }>;
  videoUrl?: string;
  imageUrl?: string;
  additionalImageUrl?: string;
}
interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceKey?: string;
  fallbackData?: ServiceData;
}
const ServiceModal: React.FC<ServiceModalProps> = ({
  isOpen,
  onClose,
  serviceKey,
  fallbackData
}) => {
  const { t } = useLanguage();
  
  // Map service keys to translation keys
  const getServiceData = (): ServiceData | null => {
    if (!serviceKey) return null;
    
    const serviceDataMap: Record<string, { translationKey: keyof typeof t.serviceData; videoUrl?: string; imageUrl?: string; additionalImageUrl?: string }> = {
      "Chronic Disease Telemedicine": {
        translationKey: "chronicDiseaseTelemedicine",
        videoUrl: "https://www.youtube.com/embed/wow5AGRB9Eo",
        imageUrl: "/lovable-uploads/b4afb37f-dd9e-499b-874e-91b7c1f0c1ad.png"
      },
      "Digital Outpatient Clinic": {
        translationKey: "digitalOutpatientClinic",
        imageUrl: "/lovable-uploads/0ba642e9-4336-4908-8150-f49a661479cb.png"
      },
      "Emergency & Panic Button": {
        translationKey: "emergencyPanicButton",
        imageUrl: "/lovable-uploads/2443acd8-5607-4550-b993-245cbefbf190.png"
      },
      "Mental Health Support": {
        translationKey: "mentalHealthSupport",
        imageUrl: "/lovable-uploads/mental-health-support.png"
      },
      "Pregnancy Monitoring & Support": {
        translationKey: "pregnancyMonitoring",
        imageUrl: "/lovable-uploads/42f212d6-0168-4d05-b6c7-7253915338e3.png"
      },
      "Marine Telemedicine": {
        translationKey: "marineTelemedicine",
        imageUrl: "/lovable-uploads/664cd28b-4994-4158-b6ce-1fdaf5fd4f44.png"
      },
      "Behavioural Change Solutions": {
        translationKey: "behaviouralChangeSolutions",
        videoUrl: "https://www.youtube.com/embed/zR-W0YCqQxU",
        imageUrl: "/lovable-uploads/behavioral-change-app.png"
      },
      "Corporate Wellbeing": {
        translationKey: "corporateWellbeing",
        imageUrl: "/lovable-uploads/corporate-wellbeing-app.png"
      },
      "Scientific Wellness": {
        translationKey: "scientificWellness"
      },
      "eHealth & Wellness Tourism": {
        translationKey: "ehealthWellnessTourism",
        videoUrl: "https://www.youtube.com/embed/Anke5PpHTz4"
      },
      "InsurTech": {
        translationKey: "insurtech",
        imageUrl: "/lovable-uploads/d6809d10-7f85-4170-9330-19ed0fd80db8.png"
      },
      "Digital Therapeutics & Pharma": {
        translationKey: "digitalTherapeuticsPharma",
        imageUrl: "/lovable-uploads/7a761384-25f9-43a3-8654-b21a8094757b.png"
      },
      "Digital Animal Health": {
        translationKey: "digitalAnimalHealth",
        imageUrl: "/lovable-uploads/4b9f520c-83c0-4c4d-8922-9e137d791ad0.png",
        additionalImageUrl: "/lovable-uploads/ab52c4db-f5e6-422d-af2d-14bea0fdbc15.png"
      },
      "Eco-Health Analytics": {
        translationKey: "ecoHealthAnalytics",
        imageUrl: "/lovable-uploads/1a599d61-0148-4686-b50a-ec8eeddbcf65.png"
      },
      "AI Decision Support": {
        translationKey: "aiDecisionSupport",
        additionalImageUrl: "/lovable-uploads/ea404312-774e-4545-9431-b60a559d95ee.png"
      },
      "DRG decision support": {
        translationKey: "drgDecisionSupport",
        imageUrl: "/lovable-uploads/b89a7215-9760-4b02-ba42-d1219d9353c6.png"
      },
      "Digital Health Consulting": {
        translationKey: "digitalHealthConsulting",
        additionalImageUrl: "/lovable-uploads/0e9fd9f5-33a8-4929-af49-613c984aadb9.png"
      }
    };
    
    const mapping = serviceDataMap[serviceKey];
    if (!mapping) return null;
    
    const translatedData = t.serviceData[mapping.translationKey];
    return {
      title: serviceKey,
      description: translatedData.description,
      features: translatedData.features,
      videoUrl: mapping.videoUrl,
      imageUrl: mapping.imageUrl,
      additionalImageUrl: mapping.additionalImageUrl
    };
  };
  
  const currentService = getServiceData() || fallbackData;
  
  if (!currentService) return null;

  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-3xl max-h-[75vh] sm:max-h-[80vh] overflow-y-auto mx-auto bg-white border border-primary/20 shadow-2xl rounded-lg p-4 sm:p-6">
        <DialogHeader className="relative">
          <DialogTitle className="sr-only">{t.serviceModal.title}</DialogTitle>
          <DialogDescription className="sr-only" id="service-dialog-description">{t.serviceModal.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* YouTube Video */}
          {currentService.videoUrl && currentService.title !== "eHealth & Wellness Tourism" && <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-neutral-900">
              <iframe src={currentService.videoUrl} title="Vidavo Healthcare Solutions" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>}

          {/* Description below video for services without images */}
          {currentService.videoUrl && !currentService.imageUrl}

           {/* Mobile App Showcase */}
           {currentService.imageUrl && currentService.title !== "InsurTech" && currentService.title !== "Eco-Health Analytics" && currentService.title !== "Digital Animal Health" && currentService.title !== "AI Decision Support" && currentService.title !== "Digital Health Consulting" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
               <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {currentService.description}                  </p>
                  
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    {!["Chronic Disease Telemedicine", "Digital Outpatient Clinic", "Emergency & Panic Button", "Pregnancy Monitoring & Support", "Mental Health Support", "Marine Telemedicine", "InsurTech", "Eco-Health Analytics", "Digital Animal Health", "Digital Therapeutics & Pharma", "DRG decision support", "Behavioural Change Solutions", "Corporate Wellbeing"].includes(currentService.title) && <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-xl scale-105" />}
                    <img src={
                      currentService.title === "Marine Telemedicine" 
                        ? marineTelemedicineDevices 
                        : currentService.title === "Pregnancy Monitoring & Support"
                        ? pregnancyMonitoringApp
                        : currentService.title === "Emergency & Panic Button"
                        ? emergencyPanicButton
                        : currentService.title === "Digital Outpatient Clinic"
                        ? digitalOutpatientClinic
                        : currentService.title === "Chronic Disease Telemedicine"
                        ? chronicDiseaseTelemedicine
                        : currentService.title === "Behavioural Change Solutions"
                        ? behaviouralChangeSolutions
                        : currentService.imageUrl
                    } alt={t.serviceModal.altTexts.mobileHealthApp} className={`relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto ${["Chronic Disease Telemedicine", "Digital Outpatient Clinic", "Emergency & Panic Button", "Pregnancy Monitoring & Support", "Mental Health Support", "Marine Telemedicine", "InsurTech", "Eco-Health Analytics", "Digital Animal Health", "Digital Therapeutics & Pharma", "DRG decision support", "Behavioural Change Solutions", "Corporate Wellbeing"].includes(currentService.title) ? "" : "shadow-2xl"}`} />
                  </div>
                </div>
              </div>
            </div>}

          {/* Introductory Text */}
          {currentService.title === "InsurTech" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t.serviceModal.specificDescriptions.insurtech}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img src={insurtechDashboard} alt={t.serviceModal.altTexts.insurtechDashboard} className="relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Eco-Health Analytics Section */}
          {currentService.title === "Eco-Health Analytics" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t.serviceModal.specificDescriptions.ecoHealth}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img src={ecoHealthAnalytics} alt={t.serviceModal.altTexts.ecoHealthDashboard} className="relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Digital Animal Health Section */}
          {currentService.title === "Digital Animal Health" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {currentService.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img src={currentService.additionalImageUrl} alt={t.serviceModal.altTexts.digitalAnimalHealth} className="relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* AI Decision Support Section */}
          {currentService.title === "AI Decision Support" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t.serviceModal.specificDescriptions.aiDecisionSupport}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img src={aiDecisionSupport} alt={t.serviceModal.altTexts.aiDecisionSupport} className="relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Digital Health Consulting Section */}
          {currentService.title === "Digital Health Consulting" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t.serviceModal.specificDescriptions.digitalHealthConsulting}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img src={currentService.additionalImageUrl} alt={t.serviceModal.altTexts.digitalHealthConsulting} className="relative z-10 w-full h-auto max-w-[320px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Scientific Wellness Section */}
          {currentService.title === "Scientific Wellness" && <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                <div className="flex-1 space-y-4">
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t.serviceModal.specificDescriptions.scientificWellness}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                     <img src={scientificWellnessApp} alt={t.serviceModal.altTexts.scientificWellness} className="relative z-10 w-full h-auto max-w-[240px] md:max-w-[280px] rounded-2xl mx-auto" />
                  </div>
                </div>
              </div>
            </div>}

          {/* eHealth & Wellness Tourism Section */}
          {currentService.title === "eHealth & Wellness Tourism" && <>
              {/* YouTube Video */}
              <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-neutral-900">
                <iframe src="https://www.youtube.com/embed/Anke5PpHTz4" title="eHealth & Wellness Tourism - Vidavo Healthcare Solutions" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              
              {/* Description with Image */}
              <div className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent-teal/5 border border-primary/10 rounded-2xl p-4 sm:p-6 overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
                <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6">
                  <div className="flex-1 space-y-4">
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      {t.serviceModal.specificDescriptions.ehealthWellnessTourism}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <img src={ehealthWellnessTourism} alt={t.serviceModal.altTexts.ehealthWellnessTourism} className="relative z-10 w-full h-auto max-w-[240px] md:max-w-[280px] rounded-2xl mx-auto" />
                    </div>
                  </div>
                </div>
              </div>
            </>}
          

          {/* Feature Blocks */}
          <div className="grid md:grid-cols-2 gap-4">
            {currentService.features.map((feature, index) => <Card key={index} className="bg-white/90 border border-primary/10 hover:border-primary/20 transition-all duration-300">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold text-primary">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default ServiceModal;